/**
 * Local snapshot store (protocol v2).
 *
 * The v2 snapshot carries the sync cursor and the dirty set *together with*
 * the data they describe — the v1 lesson (cache and data drifting apart)
 * does not recur. A v1 snapshot is migrated on first read: records keep
 * their content, the cursor starts at 0 (full first pull), and anything
 * edited after the v1 `lastSyncAt` is pre-marked dirty so the pull can never
 * silently clobber it.
 */
import type { FolderRecord, NoteRecord } from './dto.js';
import type { LibraryStateV2, PendingDelete } from './v2.js';

const STORAGE_KEY = 'jnote.local.v2';
const LEGACY_KEY = 'jnote.local.v1';
const SNAPSHOT_VERSION = 2;

export interface LocalSnapshotV2 {
  version: 2;
  folders: FolderRecord[];
  notes: NoteRecord[];
  cursor: number;
  lastSyncAt: number | null;
  dirty: string[];
  pendingDeletes: PendingDelete[];
}

export function emptyLibraryState(): LibraryStateV2 {
  return {
    folders: [],
    notes: [],
    cursor: 0,
    dirty: new Set<string>(),
    pendingDeletes: [],
    pendingAttachments: [],
    conflicts: new Map(),
  };
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

const ISO_LIKE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

function reviveDates(value: unknown): unknown {
  if (typeof value === 'string') {
    return ISO_LIKE.test(value) ? new Date(value) : value;
  }
  if (Array.isArray(value)) return value.map(reviveDates);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      out[key] = reviveDates(inner);
    }
    return out;
  }
  return value;
}

function reviveRecords<T>(raw: unknown): T[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => reviveDates(item)) as T[];
}

/** Loads the v2 snapshot, migrating a v1 snapshot (or starting empty). */
export function loadLibraryState(): LibraryStateV2 {
  const store = storage();
  if (!store) return emptyLibraryState();

  let raw: string | null = null;
  try {
    raw = store.getItem(STORAGE_KEY) ?? store.getItem(LEGACY_KEY);
  } catch {
    return emptyLibraryState();
  }
  if (!raw) return emptyLibraryState();

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed['version'] === SNAPSHOT_VERSION) {
      const state = emptyLibraryState();
      state.folders = reviveRecords<FolderRecord>(parsed['folders']);
      state.notes = reviveRecords<NoteRecord>(parsed['notes']);
      state.cursor = typeof parsed['cursor'] === 'number' ? parsed['cursor'] : 0;
      state.dirty = new Set(Array.isArray(parsed['dirty']) ? (parsed['dirty'] as string[]) : []);
      state.pendingDeletes = Array.isArray(parsed['pendingDeletes'])
        ? (parsed['pendingDeletes'] as PendingDelete[])
        : [];
      return state;
    }

    if (parsed['version'] === 1) {
      // v1 → v2 migration: unknown versions, cursor 0 (full re-pull), and any
      // record newer than the last v1 sync is dirty so its edit survives the
      // first v2 pull.
      const state = emptyLibraryState();
      state.folders = reviveRecords<FolderRecord>(parsed['folders']);
      state.notes = reviveRecords<NoteRecord>(parsed['notes']);
      const lastSyncAt =
        typeof parsed['lastSyncAt'] === 'string' ? Date.parse(parsed['lastSyncAt']) : 0;
      for (const note of state.notes) {
        if (!note.deletedAt && note.updatedAt.getTime() > lastSyncAt) {
          state.dirty.add(`note:${note.id}`);
        }
      }
      for (const folder of state.folders) {
        if (folder.updatedAt.getTime() > lastSyncAt) state.dirty.add(`folder:${folder.id}`);
      }
      return state;
    }
    return emptyLibraryState();
  } catch {
    return emptyLibraryState();
  }
}

/** Persists the v2 snapshot. Best-effort: quota/private mode never blocks sync. */
export function saveLibraryState(
  state: LibraryStateV2,
  lastSyncAt: number | null,
): boolean {
  const store = storage();
  if (!store) return false;
  const snapshot: LocalSnapshotV2 = {
    version: SNAPSHOT_VERSION,
    folders: state.folders,
    notes: state.notes,
    cursor: state.cursor,
    lastSyncAt,
    dirty: [...state.dirty],
    pendingDeletes: state.pendingDeletes,
  };
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
}

/** Drops both snapshot generations (sign-out, hard reset). */
export function clearLibraryState(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
    store.removeItem(LEGACY_KEY);
  } catch {
    // Best-effort.
  }
}
